import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useRef, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  OPERATIONAL_QUERY_OPTIONS,
  ADMIN_QUERY_KEYS,
  invalidateAllAdminState,
  invalidateAllTLState,
} from "@/lib/api/query-options";
import {
  Users,
  BookOpen,
  Map,
  UserCircle,
  ShieldAlert,
  ArrowLeft,
  CheckCircle2,
  ChevronRight,
  GraduationCap,
  Download,
  Upload,
  FileText,
  AlertCircle,
  Search,
  X,
  Plus,
  UserPlus,
  UserRoundPlus,
  Loader2,
  XCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { TableSkeleton } from "@/components/loading";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { toast } from "sonner";
import {
  getTeamById,
  fetchTeamCourseAssignmentsAll,
  fetchTeamTrackAssignmentsAll,
  fetchActiveTeamLeaders,
  assignTeamTrack,
  assignTeamCourse,
  addTeamMember,
  removeTeamMember,
  removeTeamTrack,
  removeTeamCourse,
} from "@/lib/api/teams";
import {
  fetchGroupStudents,
  fetchStudentGrid,
  importGroupStudentsCsv,
  downloadStudentsCsvTemplate,
  createStudent,
  STUDY_LEVELS,
  ImportStudentsResponse,
} from "@/lib/api/students";
import { downloadImportErrorsCsv } from "@/lib/import-error-csv";
import { fetchAllCourses } from "@/lib/api/courses";
import { AssignableContentPicker } from "@/components/team/assignable-content-picker";
import { fetchLearningTracks } from "@/lib/api/enrollment";
import { RequirePermission } from "@/components/auth/RequirePermission";
import { AccessDenied } from "@/components/auth/AccessDenied";
import { leaderLabel } from "@/components/admin/groups/leaders";

export const Route = createFileRoute("/_authenticated/admin/groups_/$groupId")({
  component: () => (
    <RequirePermission
      action="manage:users"
      fallback={<AccessDenied message="Group management is limited to System Admins." />}
    >
      <GroupDetailsPage />
    </RequirePermission>
  ),
});

function GroupDetailsPage() {
  const { groupId } = Route.useParams();

  // 1. Fetch Basic Data
  const { data: group, isLoading: isGroupLoading } = useQuery({
    queryKey: ["team", groupId],
    queryFn: () => getTeamById(groupId),
    retry: false,
  });

  const { data: leaders = [] } = useQuery({
    queryKey: ["active-team-leaders"],
    queryFn: fetchActiveTeamLeaders,
    retry: false,
  });

  const { data: allCourses = [] } = useQuery({
    queryKey: ["all-courses"],
    queryFn: fetchAllCourses,
    retry: false,
  });

  const { data: allTracks = [] } = useQuery({
    queryKey: ["learning-tracks"],
    queryFn: fetchLearningTracks,
    retry: false,
  });

  // 2. Fetch Assignments
  const queryClient = useQueryClient();

  const [studentPage, setStudentPage] = useState(1);
  const [studentSearch, setStudentSearch] = useState("");
  const [studentSearchInput, setStudentSearchInput] = useState("");
  const studentLimit = 10;

  // Local queries for sidebar filtering
  const [trackQuery, setTrackQuery] = useState("");
  const [courseQuery, setCourseQuery] = useState("");

  // Live search debounce for students list
  useEffect(() => {
    const timer = setTimeout(() => {
      if (studentSearch !== studentSearchInput) {
        setStudentSearch(studentSearchInput);
        setStudentPage(1);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [studentSearchInput, studentSearch]);

  const { data: studentsData = { items: [], totalCount: 0 }, isLoading: isStudentsLoading } =
    useQuery({
      queryKey: ["group-students", groupId, studentPage, studentSearch],
      queryFn: () => fetchGroupStudents(groupId, studentPage, studentLimit, studentSearch),
      retry: false,
    });

  const groupStudents = studentsData.items || [];
  const studentsCount = studentsData.totalCount || 0;
  const totalStudentPages = Math.ceil(studentsCount / studentLimit);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setStudentSearch(studentSearchInput);
    setStudentPage(1);
  };

  const handleClearSearch = () => {
    setStudentSearchInput("");
    setStudentSearch("");
    setStudentPage(1);
  };

  const { data: allCourseAssignments = [] } = useQuery({
    queryKey: ADMIN_QUERY_KEYS.teamCourseAssignmentsAll,
    queryFn: fetchTeamCourseAssignmentsAll,
    retry: false,
    ...OPERATIONAL_QUERY_OPTIONS,
  });

  const { data: allTrackAssignments = [] } = useQuery({
    queryKey: ADMIN_QUERY_KEYS.teamTrackAssignmentsAll,
    queryFn: fetchTeamTrackAssignmentsAll,
    retry: false,
    ...OPERATIONAL_QUERY_OPTIONS,
  });

  // 3. Process Data
  const isLoading = isGroupLoading;

  // Helper for professional pagination
  const renderPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalStudentPages <= maxVisiblePages) {
      for (let i = 1; i <= totalStudentPages; i++) {
        pages.push(
          <PaginationItem key={i}>
            <PaginationLink
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setStudentPage(i);
              }}
              isActive={studentPage === i}
            >
              {i}
            </PaginationLink>
          </PaginationItem>,
        );
      }
    } else {
      let startPage = Math.max(1, studentPage - 1);
      let endPage = Math.min(totalStudentPages, studentPage + 1);

      if (studentPage <= 2) {
        endPage = 3;
      } else if (studentPage >= totalStudentPages - 1) {
        startPage = totalStudentPages - 2;
      }

      if (startPage > 1) {
        pages.push(
          <PaginationItem key={1}>
            <PaginationLink
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setStudentPage(1);
              }}
            >
              1
            </PaginationLink>
          </PaginationItem>,
        );
        if (startPage > 2) {
          pages.push(
            <PaginationItem key="ellipsis-start">
              <PaginationEllipsis />
            </PaginationItem>,
          );
        }
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(
          <PaginationItem key={i}>
            <PaginationLink
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setStudentPage(i);
              }}
              isActive={studentPage === i}
            >
              {i}
            </PaginationLink>
          </PaginationItem>,
        );
      }

      if (endPage < totalStudentPages) {
        if (endPage < totalStudentPages - 1) {
          pages.push(
            <PaginationItem key="ellipsis-end">
              <PaginationEllipsis />
            </PaginationItem>,
          );
        }
        pages.push(
          <PaginationItem key={totalStudentPages}>
            <PaginationLink
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setStudentPage(totalStudentPages);
              }}
            >
              {totalStudentPages}
            </PaginationLink>
          </PaginationItem>,
        );
      }
    }
    return pages;
  };

  // Filter and map assignments for this group
  // The backend assignment DTOs key the group as `team_id` (not `group_id`).
  const groupCourseAssigns = allCourseAssignments.filter(
    (ca: any) => (ca.team_id ?? ca.group_id) === groupId,
  );
  const groupCourses = groupCourseAssigns.map((ca: any) => {
    const course = allCourses.find((c: any) => c.id === ca.course_id);
    return { ...ca, course };
  });

  const groupTrackAssigns = allTrackAssignments.filter(
    (ta: any) => (ta.team_id ?? ta.group_id) === groupId,
  );
  const groupTracks = groupTrackAssigns.map((ta: any) => {
    const track = allTracks.find((t: any) => t.id === ta.track_id);
    return { ...ta, track };
  });

  // A group is led by a set of users, all with identical authority — render every one of them.
  // `leaders` (the appointable-leader list) is only a fallback name source for payloads that
  // carried ids without names.
  const groupLeaders = useMemo(() => {
    const assigned = group?.leaders ?? [];
    return assigned.map((l: any) => ({
      ...l,
      name: l.name ?? leaders.find((a: any) => a.id === l.id)?.full_name ?? null,
      email: l.email ?? leaders.find((a: any) => a.id === l.id)?.email ?? null,
    }));
  }, [group, leaders]);

  // Client-side filtering of Sidebar tracks/courses
  const filteredGroupTracks = useMemo(() => {
    if (!trackQuery.trim()) return groupTracks;
    const q = trackQuery.toLowerCase();
    return groupTracks.filter((t: any) => t.track?.name?.toLowerCase().includes(q));
  }, [groupTracks, trackQuery]);

  const filteredGroupCourses = useMemo(() => {
    if (!courseQuery.trim()) return groupCourses;
    const q = courseQuery.toLowerCase();
    return groupCourses.filter((c: any) => c.course?.title?.toLowerCase().includes(q));
  }, [groupCourses, courseQuery]);

  // Options for the assign pickers (exclude what's already assigned/enrolled).
  const [assignTrackOpen, setAssignTrackOpen] = useState(false);
  const [assignCourseOpen, setAssignCourseOpen] = useState(false);
  const [addStudentOpen, setAddStudentOpen] = useState(false);

  // Add Student dialog: lazy, debounced, server-side-searched, paginated picker
  // (was previously backed by an always-on, org-wide fetchAllProfiles() call).
  const [addStudentSearchInput, setAddStudentSearchInput] = useState("");
  const [addStudentSearch, setAddStudentSearch] = useState("");
  const [addStudentPickerPage, setAddStudentPickerPage] = useState(1);
  const addStudentPickerPageSize = 20;

  useEffect(() => {
    const timer = setTimeout(() => {
      if (addStudentSearch !== addStudentSearchInput) {
        setAddStudentSearch(addStudentSearchInput);
        setAddStudentPickerPage(1);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [addStudentSearchInput, addStudentSearch]);

  const {
    data: addStudentPickerData = { items: [], totalCount: 0 },
    isLoading: isAddStudentPickerLoading,
  } = useQuery({
    queryKey: ["group-add-student-picker", groupId, addStudentPickerPage, addStudentSearch],
    queryFn: () =>
      fetchStudentGrid({
        pageNumber: addStudentPickerPage,
        pageSize: addStudentPickerPageSize,
        search: addStudentSearch,
      }),
    enabled: addStudentOpen,
    retry: false,
  });

  // Only members of THIS group are filtered out — a student already in another group is still
  // addable here, since group membership is many-to-many.
  const currentMemberIds = useMemo(
    () => new Set(groupStudents.map((s: any) => s.id)),
    [groupStudents],
  );
  const availableStudents = useMemo(
    () => (addStudentPickerData.items || []).filter((u: any) => !currentMemberIds.has(u.id)),
    [addStudentPickerData, currentMemberIds],
  );
  const addStudentTotalPages = Math.max(
    1,
    Math.ceil((addStudentPickerData.totalCount || 0) / addStudentPickerPageSize),
  );

  const [isImportOpen, setIsImportOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importResult, setImportResult] = useState<ImportStudentsResponse | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Create one student ────────────────────────────────────────────────────
  // "Add Student" only picks an account that already exists, so the sole way to get a NEW student
  // into a group was to write a one-row spreadsheet and import it. Same endpoint the importer uses
  // per row (POST /Students with groupId), so the resulting account is identical either way.
  const emptyNewStudent = { email: "", nationalId: "", studentCode: "", studyLevel: 0 };
  const [createStudentOpen, setCreateStudentOpen] = useState(false);
  const [newStudent, setNewStudent] = useState(emptyNewStudent);

  // The National ID is also the initial password, so a wrong one locks the student out of an account
  // that otherwise looks fine. Checked here as well as server-side to say so before the round trip.
  const nationalIdDigits = newStudent.nationalId.replace(/\D/g, "");
  const nationalIdValid = /^\d{14}$/.test(newStudent.nationalId.trim());
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newStudent.email.trim());

  const createStudentMut = useMutation({
    mutationFn: () =>
      createStudent({
        email: newStudent.email,
        nationalId: newStudent.nationalId,
        studentCode: newStudent.studentCode,
        studyLevel: newStudent.studyLevel,
        groupId,
      }),
    onSuccess: async () => {
      toast.success(`${newStudent.email.trim()} created and added to this group`);
      setCreateStudentOpen(false);
      setNewStudent(emptyNewStudent);
      await handleDetailMutationSuccess();
      queryClient.invalidateQueries({ queryKey: ["group-students", groupId] });
    },
    onError: (e: any) => toast.error(e?.message || "Failed to create student"),
  });

  const importMut = useMutation({
    mutationFn: (file: File) => importGroupStudentsCsv({ groupId, file }),
    onSuccess: (res) => {
      setImportResult(res);
      if (res.failureCount === 0) {
        toast.success("CSV import successful");
      } else {
        toast.warning(`Imported with ${res.failureCount} errors`);
      }
      queryClient.invalidateQueries({ queryKey: ["group-students", groupId] });
    },
    onError: () => {
      toast.error("Failed to import CSV");
    },
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedFile(e.target.files[0]);
      setImportResult(null); // reset previous result
    }
  };

  const handleImportSubmit = () => {
    if (!selectedFile) return;
    importMut.mutate(selectedFile);
  };

  const handleDetailMutationSuccess = async () => {
    await Promise.all([invalidateAllAdminState(queryClient), invalidateAllTLState(queryClient)]);
  };

  // ── Assign track / course / student ───────────────────────────────────────
  const assignTrackMut = useMutation({
    mutationFn: (trackId: string) => assignTeamTrack(groupId, trackId),
    onSuccess: async (result: any) => {
      // Overlap with the group's direct course links succeeds WITH warnings — surfaced rather than
      // swallowed, because the duplicate is what makes certificate precedence order-dependent.
      const warnings: string[] = result?.warnings ?? [];
      if (warnings.length > 0) warnings.forEach((w) => toast.warning(w));
      else toast.success("Track assigned");
      await handleDetailMutationSuccess();
      setAssignTrackOpen(false);
    },
    onError: (e: any) => toast.error(e?.message || "Failed to assign track"),
  });

  const assignCourseMut = useMutation({
    mutationFn: (courseId: string) => assignTeamCourse(groupId, courseId),
    onSuccess: async () => {
      toast.success("Course assigned");
      await handleDetailMutationSuccess();
      setAssignCourseOpen(false);
    },
    onError: (e: any) => toast.error(e?.message || "Failed to assign course"),
  });

  const addStudentMut = useMutation({
    mutationFn: (studentId: string) => addTeamMember(groupId, studentId),
    onSuccess: async () => {
      toast.success("Student added to group");
      await handleDetailMutationSuccess();
      setAddStudentOpen(false);
    },
    onError: (e: any) => toast.error(e?.message || "Failed to add student"),
  });

  const removeStudentMut = useMutation({
    mutationFn: (studentId: string) => removeTeamMember(groupId, studentId),
    onSuccess: async () => {
      toast.success("Student removed from group");
      // Removing the last row of a page would otherwise leave an empty page.
      if (groupStudents.length === 1 && studentPage > 1) {
        setStudentPage(studentPage - 1);
      }
      await handleDetailMutationSuccess();
    },
    onError: (e: any) => toast.error(e?.message || "Failed to remove student"),
  });

  const removeTrackMut = useMutation({
    mutationFn: (trackId: string) => removeTeamTrack(groupId, trackId),
    onSuccess: async () => {
      toast.success("Track removed from group");
      await handleDetailMutationSuccess();
    },
    onError: (e: any) => toast.error(e?.message || "Failed to remove track"),
  });

  const removeCourseMut = useMutation({
    mutationFn: (courseId: string) => removeTeamCourse(groupId, courseId),
    onSuccess: async () => {
      toast.success("Course removed from group");
      await handleDetailMutationSuccess();
    },
    onError: (e: any) => toast.error(e?.message || "Failed to remove course"),
  });

  if (isLoading) {
    return (
      <div className="space-y-6 p-6">
        <Skeleton className="h-40 w-full rounded-3xl animate-pulse" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Skeleton className="h-[400px] lg:col-span-2 rounded-3xl animate-pulse" />
          <Skeleton className="h-[400px] rounded-3xl animate-pulse" />
        </div>
      </div>
    );
  }

  if (!group) {
    return (
      <div className="p-16 text-center bg-card rounded-3xl border border-dashed flex flex-col items-center m-6 animate-in fade-in duration-300">
        <ShieldAlert className="h-12 w-12 text-destructive mb-4 animate-bounce" />
        <h2 className="text-xl font-bold">Group Not Found</h2>
        <p className="text-muted-foreground mt-2">
          The group you are looking for does not exist or has been deleted.
        </p>
        <Button
          asChild
          className="mt-6 rounded-xl bg-primary hover:bg-primary/95 text-primary-foreground"
        >
          <Link to="/admin/groups">Back to Groups</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-8 pb-10 p-6 font-sans">
      {/* Navigation */}
      <div className="animate-in fade-in duration-300">
        <Link
          to="/admin/groups"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors group"
        >
          <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" />
          Back to Groups
        </Link>
      </div>

      {/* Header Banner using Brand Palette (Navy to Gold Accent) - animated entrance */}
      <div className="relative overflow-hidden bg-gradient-to-br from-primary via-primary/95 to-accent/90 text-white rounded-3xl p-8 shadow-lg animate-in fade-in slide-in-from-top-4 duration-500">
        {/* Decorative background shape */}
        <div className="absolute top-0 right-0 -translate-y-12 translate-x-1/3 opacity-10 pointer-events-none">
          <Users
            className="w-64 h-64 text-white animate-pulse"
            style={{ animationDuration: "8s" }}
          />
        </div>

        <div className="relative z-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-white/10 backdrop-blur-md rounded-full text-xs font-semibold mb-4 border border-white/25 shadow-sm">
                <CheckCircle2 className="h-3.5 w-3.5 text-accent animate-bounce" />
                Active Group
              </div>
              <h1 className="text-4xl font-extrabold tracking-tight mb-2 drop-shadow-sm">
                {group.name}
              </h1>
              <p className="text-primary-foreground/85 max-w-2xl leading-relaxed text-sm md:text-base">
                {group.description || "No specific description provided for this group."}
              </p>
            </div>

            {/* Leader Card in Header with Glassmorphism Overlay */}
            <div className="bg-white/10 backdrop-blur-md border border-white/25 p-4 rounded-2xl flex items-center gap-4 min-w-[250px] max-w-sm shadow-sm hover:bg-white/20 transition-all duration-300 hover:scale-102">
              <div className="bg-white/20 p-3 rounded-full shadow-inner shrink-0">
                <UserCircle className="h-6 w-6 text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] text-accent font-bold uppercase tracking-widest mb-0.5">
                  {groupLeaders.length > 1
                    ? `Team Leaders (${groupLeaders.length})`
                    : "Team Leader"}
                </p>
                {groupLeaders.length === 0 ? (
                  <p className="font-bold text-white text-lg leading-tight">Unassigned</p>
                ) : (
                  <div className="space-y-0.5">
                    {groupLeaders.map((l: any) => (
                      <p
                        key={l.id}
                        className="font-bold text-white text-lg leading-tight truncate"
                        title={leaderLabel(l)}
                      >
                        {leaderLabel(l)}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Dashboard Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content (Students) */}
        <div
          className="lg:col-span-2 space-y-6 animate-in fade-in slide-in-from-left-4 duration-500"
          style={{ animationDelay: "100ms", animationFillMode: "both" }}
        >
          <div className="bg-card border rounded-3xl shadow-sm overflow-hidden flex flex-col">
            <div className="p-6 border-b flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold flex items-center gap-2 text-foreground">
                  <GraduationCap className="h-5 w-5 text-primary" />
                  Enrolled Students
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  {studentsCount} {studentsCount === 1 ? "student" : "students"} in this group
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => {
                    setAddStudentSearchInput("");
                    setAddStudentSearch("");
                    setAddStudentPickerPage(1);
                    setAddStudentOpen(true);
                  }}
                  className="gap-2 rounded-xl bg-primary hover:bg-primary/95 text-primary-foreground font-semibold shadow-sm cursor-pointer transition-transform duration-300 hover:scale-102"
                >
                  <UserPlus className="h-4 w-4" /> Add Student
                </Button>
                {/* Distinct from "Add Student", which searches accounts that already exist. This
                    one registers a brand-new student straight into the group. */}
                <Button
                  variant="outline"
                  onClick={() => {
                    setNewStudent(emptyNewStudent);
                    setCreateStudentOpen(true);
                  }}
                  className="gap-2 rounded-xl font-semibold shadow-sm cursor-pointer transition-transform duration-300 hover:scale-102"
                >
                  <UserRoundPlus className="h-4 w-4" /> Create Student
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsImportOpen(true);
                    setImportResult(null);
                    setSelectedFile(null);
                  }}
                  className="gap-2 rounded-xl font-semibold shadow-sm cursor-pointer transition-transform duration-300 hover:scale-102"
                >
                  <Upload className="h-4 w-4" /> Import CSV
                </Button>
              </div>
            </div>

            {/* Search Bar */}
            <div className="p-4 border-b bg-muted/5 flex items-center justify-between gap-4">
              <form onSubmit={handleSearchSubmit} className="relative w-full sm:w-72">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search students by email..."
                  value={studentSearchInput}
                  onChange={(e) => setStudentSearchInput(e.target.value)}
                  className="pl-9 pr-8 rounded-xl bg-card border-muted/80 focus-visible:ring-primary h-9 text-xs text-foreground transition-all focus-visible:ring-2 focus-visible:ring-primary/20"
                />
                {studentSearchInput && (
                  <button
                    type="button"
                    onClick={handleClearSearch}
                    className="absolute right-2.5 top-2.5 text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </form>
            </div>

            <div className="p-0">
              {isStudentsLoading ? (
                <div className="p-4">
                  <TableSkeleton rows={5} columns={2} />
                </div>
              ) : groupStudents.length === 0 ? (
                <div className="p-12 text-center text-muted-foreground bg-muted/10 animate-in fade-in duration-300">
                  <GraduationCap className="h-10 w-10 mx-auto mb-3 opacity-20 text-primary animate-pulse" />
                  <p>No students assigned to this group yet.</p>
                </div>
              ) : (
                <div className="divide-y">
                  {groupStudents.map((s: any, index: number) => {
                    const joinedDate =
                      s.groups?.find((g: any) => g.groupId === groupId)?.joinedAt || s.createdAt;
                    return (
                      <div
                        key={s.id}
                        className="flex items-center justify-between p-4 hover:bg-muted/30 transition-colors animate-in fade-in duration-300"
                        style={{ animationDelay: `${index * 35}ms`, animationFillMode: "both" }}
                      >
                        <div className="flex items-center gap-4">
                          <div className="h-10 w-10 bg-primary/10 text-primary rounded-full flex items-center justify-center font-bold text-sm shadow-sm border border-primary/20 uppercase transition-transform duration-300 hover:scale-105">
                            {(s.fullName || s.email || "?").charAt(0)}
                          </div>
                          <div>
                            {/* StudentDto now carries FullName, so the roster can lead with the
                                student's name instead of their email address. */}
                            <p className="font-semibold text-sm text-foreground">
                              {s.fullName || s.email || "Unknown User"}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {s.fullName && s.email ? s.email : null}
                              {s.fullName && s.email && s.studentCode ? " · " : null}
                              {s.studentCode ? `Code: ${s.studentCode}` : null}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <div className="text-[10px] font-medium text-muted-foreground bg-muted/50 px-2.5 py-1 rounded-md border border-muted/70">
                            Joined:{" "}
                            {new Date(joinedDate || new Date()).toLocaleDateString(undefined, {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })}
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Remove from group"
                            disabled={removeStudentMut.isPending}
                            onClick={() => {
                              if (
                                confirm(
                                  `Remove ${s.email || "this student"} from this group? Course/track assignments they got via this group will be revoked; their progress and any issued certificates are kept.`,
                                )
                              ) {
                                removeStudentMut.mutate(s.id);
                              }
                            }}
                            className="h-7 w-7 text-muted-foreground hover:bg-destructive/10 hover:text-destructive cursor-pointer"
                          >
                            {removeStudentMut.isPending && removeStudentMut.variables === s.id ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <XCircle className="h-3.5 w-3.5" />
                            )}
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Pagination */}
            {totalStudentPages > 0 && (
              <div className="p-4 border-t flex flex-col sm:flex-row items-center justify-between gap-4 bg-muted/5">
                <div className="text-xs text-muted-foreground w-full sm:w-auto text-center sm:text-left">
                  Showing{" "}
                  <span className="font-medium text-foreground">{groupStudents.length}</span> of{" "}
                  <span className="font-medium text-foreground">{studentsCount}</span> students
                </div>
                <Pagination className="w-full sm:w-auto mx-0 justify-center sm:justify-end">
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          if (studentPage > 1) setStudentPage(studentPage - 1);
                        }}
                        className={
                          studentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"
                        }
                      />
                    </PaginationItem>
                    {renderPageNumbers()}
                    <PaginationItem>
                      <PaginationNext
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          if (studentPage < totalStudentPages) setStudentPage(studentPage + 1);
                        }}
                        className={
                          studentPage === totalStudentPages
                            ? "pointer-events-none opacity-50"
                            : "cursor-pointer"
                        }
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar (Courses & Tracks) */}
        <div
          className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-500"
          style={{ animationDelay: "150ms", animationFillMode: "both" }}
        >
          {/* Tracks Section */}
          <div className="bg-card border rounded-3xl shadow-sm p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold flex items-center gap-2 text-foreground">
                <Map
                  className="h-5 w-5 text-accent animate-pulse"
                  style={{ animationDuration: "4s" }}
                />
                Learning Tracks
              </h2>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setAssignTrackOpen(true)}
                className="gap-1.5 rounded-lg text-xs font-semibold h-8 cursor-pointer"
              >
                <Plus className="h-3.5 w-3.5" /> Assign
              </Button>
            </div>

            {/* Local Filter for Tracks */}
            {groupTracks.length > 0 && (
              <div className="relative mb-3">
                <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  placeholder="Filter tracks by name..."
                  value={trackQuery}
                  onChange={(e) => setTrackQuery(e.target.value)}
                  className="pl-8 pr-7 h-7.5 rounded-lg text-xs bg-muted/20 border-muted/70 focus-visible:ring-primary text-foreground"
                />
                {trackQuery && (
                  <button
                    onClick={() => setTrackQuery("")}
                    className="absolute right-2 top-2 text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
            )}

            <div className="space-y-3">
              {groupTracks.length === 0 ? (
                <div className="text-sm text-muted-foreground text-center p-4 border border-dashed rounded-xl bg-muted/10">
                  No tracks assigned.
                </div>
              ) : filteredGroupTracks.length === 0 ? (
                <div className="text-xs text-muted-foreground text-center p-4">
                  No matching tracks.
                </div>
              ) : (
                filteredGroupTracks.map((t: any, index: number) => (
                  <div
                    key={t.id}
                    className="p-3.5 border rounded-xl hover:border-accent hover:shadow-sm hover:bg-accent/5 transition-all group/item animate-in fade-in duration-300 flex items-center justify-between gap-2"
                    style={{ animationDelay: `${index * 30}ms`, animationFillMode: "both" }}
                  >
                    <p className="font-semibold text-sm group-hover/item:text-foreground transition-colors truncate">
                      {t.track?.name || "Unknown Track"}
                    </p>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        variant="ghost"
                        size="icon"
                        title="Remove from group"
                        disabled={removeTrackMut.isPending}
                        onClick={() => {
                          if (
                            confirm(
                              `Remove track "${t.track?.name || "this track"}" from this group?`,
                            )
                          ) {
                            removeTrackMut.mutate(t.track_id);
                          }
                        }}
                        className="h-7 w-7 text-muted-foreground hover:bg-muted hover:text-foreground cursor-pointer"
                      >
                        <XCircle className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Courses Section */}
          <div className="bg-card border rounded-3xl shadow-sm p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold flex items-center gap-2 text-foreground">
                <BookOpen
                  className="h-5 w-5 text-primary/70 animate-pulse"
                  style={{ animationDuration: "4s" }}
                />
                Assigned Courses
              </h2>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setAssignCourseOpen(true)}
                className="gap-1.5 rounded-lg text-xs font-semibold h-8 cursor-pointer"
              >
                <Plus className="h-3.5 w-3.5" /> Assign
              </Button>
            </div>

            {/* Local Filter for Courses */}
            {groupCourses.length > 0 && (
              <div className="relative mb-3">
                <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  placeholder="Filter courses by name..."
                  value={courseQuery}
                  onChange={(e) => setCourseQuery(e.target.value)}
                  className="pl-8 pr-7 h-7.5 rounded-lg text-xs bg-muted/20 border-muted/70 focus-visible:ring-primary text-foreground"
                />
                {courseQuery && (
                  <button
                    onClick={() => setCourseQuery("")}
                    className="absolute right-2 top-2 text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
            )}

            <div className="space-y-3">
              {groupCourses.length === 0 ? (
                <div className="text-sm text-muted-foreground text-center p-4 border border-dashed rounded-xl bg-muted/10">
                  No explicit courses assigned.
                </div>
              ) : filteredGroupCourses.length === 0 ? (
                <div className="text-xs text-muted-foreground text-center p-4">
                  No matching courses.
                </div>
              ) : (
                filteredGroupCourses.map((c: any, index: number) => (
                  <div
                    key={c.id}
                    className="p-3.5 border rounded-xl hover:border-primary/30 hover:shadow-sm hover:bg-primary/5 transition-all group/item animate-in fade-in duration-300"
                    style={{ animationDelay: `${index * 30}ms`, animationFillMode: "both" }}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-semibold text-sm group-hover/item:text-primary transition-colors truncate">
                        {c.course?.title || "Unknown Course"}
                      </p>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Remove from group"
                          disabled={removeCourseMut.isPending}
                          onClick={() => {
                            if (
                              confirm(
                                `Remove course "${c.course?.title || "this course"}" from this group?`,
                              )
                            ) {
                              removeCourseMut.mutate(c.course_id);
                            }
                          }}
                          className="h-7 w-7 text-muted-foreground hover:bg-muted hover:text-foreground cursor-pointer"
                        >
                          <XCircle className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                    {c.course?.level && (
                      <span className="text-[10px] bg-primary/10 text-primary font-bold px-2 py-0.5 rounded-md mt-2 inline-block border border-primary/20">
                        {c.course.level}
                      </span>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Import CSV Dialog */}
      <Dialog open={isImportOpen} onOpenChange={setIsImportOpen}>
        <DialogContent className="sm:max-w-md bg-card border rounded-2xl shadow-lg animate-in zoom-in-95 duration-200">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Import Students via CSV</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-primary/5 text-foreground p-4 rounded-xl border border-primary/20 flex flex-col gap-3">
              <div className="flex items-start gap-2">
                <FileText className="h-5 w-5 mt-0.5 text-primary shrink-0 animate-pulse" />
                <p className="text-sm animate-in fade-in duration-300">
                  Download the template, fill in the student emails, and upload the file to bulk
                  assign students to this group.
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => downloadStudentsCsvTemplate()}
                className="self-start gap-2 bg-background border-primary/20 hover:bg-primary/5 text-primary font-semibold rounded-xl cursor-pointer transition-transform duration-300 hover:scale-102"
              >
                <Download className="h-4 w-4" /> Download Template
              </Button>
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-semibold text-muted-foreground">
                Upload Completed CSV
              </Label>
              <Input
                type="file"
                accept=".csv"
                ref={fileInputRef}
                onChange={handleFileChange}
                className="file:mr-4 file:py-1 file:px-4 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-primary/10 file:text-primary hover:file:bg-primary/20 cursor-pointer rounded-xl text-foreground"
              />
            </div>

            {importResult && (
              <div className="mt-4 border rounded-xl overflow-hidden animate-in slide-in-from-top-4 duration-300">
                <div className="bg-muted p-3 border-b flex justify-between items-center text-sm">
                  <span className="font-bold text-foreground">Import Results</span>
                  <div className="flex gap-3 font-medium flex-wrap">
                    <span className="text-green-600">{importResult.successCount} Created</span>
                    {/* Existing students added to this group — a student can be in several
                        groups, so this is a success, not the failure it used to be counted as. */}
                    {(importResult.linkedExistingCount ?? 0) > 0 && (
                      <span className="text-green-600">
                        {importResult.linkedExistingCount} Added to group
                      </span>
                    )}
                    {(importResult.alreadyInGroupCount ?? 0) > 0 && (
                      <span className="text-muted-foreground">
                        {importResult.alreadyInGroupCount} Already in group
                      </span>
                    )}
                    <span className="text-red-500">{importResult.failureCount} Failed</span>
                  </div>
                </div>
                {importResult.errors && importResult.errors.length > 0 && (
                  <>
                    <div className="max-h-40 overflow-y-auto bg-red-50/50 p-2">
                      {importResult.errors.map((err, i) => (
                        <div key={i} className="text-xs text-red-600 flex items-start gap-1.5 p-1">
                          <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5 animate-bounce" />
                          <span>
                            Row {err.row} ({err.email}): {err.errorMessage}
                          </span>
                        </div>
                      ))}
                    </div>
                    {/* The scrollbox is fine for three bad rows and useless for forty — and it is
                        gone the moment this dialog closes. The download is the failed rows of the
                        uploaded sheet with an Error column appended, so the fix loop is: download,
                        correct the flagged cells, upload that same file again. */}
                    <div className="flex items-center justify-between gap-3 border-t bg-red-50/30 px-3 py-2">
                      <p className="text-[11px] text-muted-foreground">
                        Fix the flagged cells and re-upload the downloaded file — successful rows
                        are not repeated.
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          downloadImportErrorsCsv(
                            importResult.errors,
                            selectedFile,
                            `${(group?.name || "group").replace(/[^\w-]+/g, "_")}-import-errors.csv`,
                          )
                        }
                        className="shrink-0 gap-2 rounded-xl border-red-200 bg-background font-semibold text-red-600 hover:bg-red-50 cursor-pointer"
                      >
                        <Download className="h-4 w-4" /> Download errors (CSV)
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="ghost" onClick={() => setIsImportOpen(false)} className="rounded-xl">
              Close
            </Button>
            <Button
              onClick={handleImportSubmit}
              disabled={!selectedFile || importMut.isPending}
              className="rounded-xl bg-primary hover:bg-primary/95 text-primary-foreground font-semibold gap-2 cursor-pointer transition-transform duration-300 hover:scale-102"
            >
              {importMut.isPending ? (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              ) : (
                <Upload className="h-4 w-4" />
              )}
              {importMut.isPending ? "Importing..." : "Start Import"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create one student — the single-row counterpart to the CSV import */}
      <Dialog open={createStudentOpen} onOpenChange={setCreateStudentOpen}>
        <DialogContent className="sm:max-w-md bg-card border rounded-2xl shadow-lg animate-in zoom-in-95 duration-200">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Create Student</DialogTitle>
            <DialogDescription>
              Registers a new student account and adds it to{" "}
              <span className="font-semibold text-foreground">{group?.name || "this group"}</span>,
              inheriting the group's courses and tracks.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="new-student-email" className="text-xs font-semibold">
                Email <span className="text-red-500">*</span>
              </Label>
              <Input
                id="new-student-email"
                type="email"
                autoComplete="off"
                value={newStudent.email}
                onChange={(e) => setNewStudent({ ...newStudent, email: e.target.value })}
                placeholder="student@example.com"
                className="rounded-xl text-foreground"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="new-student-nid" className="text-xs font-semibold">
                National ID <span className="text-red-500">*</span>
              </Label>
              <Input
                id="new-student-nid"
                inputMode="numeric"
                autoComplete="off"
                value={newStudent.nationalId}
                onChange={(e) => setNewStudent({ ...newStudent, nationalId: e.target.value })}
                placeholder="14 digits"
                className="rounded-xl text-foreground"
              />
              {/* Stated up front, not discovered at first login: this value IS the password. */}
              <p
                className={`text-[11px] ${
                  newStudent.nationalId && !nationalIdValid
                    ? "text-red-500"
                    : "text-muted-foreground"
                }`}
              >
                Exactly 14 digits ({nationalIdDigits.length}/14). This is also the student's initial
                password.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="new-student-code" className="text-xs font-semibold">
                  Student Code
                </Label>
                <Input
                  id="new-student-code"
                  value={newStudent.studentCode}
                  onChange={(e) => setNewStudent({ ...newStudent, studentCode: e.target.value })}
                  placeholder="Optional"
                  className="rounded-xl text-foreground"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="new-student-level" className="text-xs font-semibold">
                  Study Level
                </Label>
                <select
                  id="new-student-level"
                  value={newStudent.studyLevel}
                  onChange={(e) =>
                    setNewStudent({ ...newStudent, studyLevel: Number(e.target.value) })
                  }
                  className="h-9 w-full rounded-xl border border-input bg-background px-3 text-sm text-foreground"
                >
                  {STUDY_LEVELS.map((l) => (
                    <option key={l.value} value={l.value}>
                      {l.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="ghost"
              onClick={() => setCreateStudentOpen(false)}
              className="rounded-xl"
            >
              Cancel
            </Button>
            <Button
              onClick={() => createStudentMut.mutate()}
              disabled={!emailValid || !nationalIdValid || createStudentMut.isPending}
              className="rounded-xl bg-primary hover:bg-primary/95 text-primary-foreground font-semibold gap-2 cursor-pointer transition-transform duration-300 hover:scale-102"
            >
              {createStudentMut.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <UserRoundPlus className="h-4 w-4" />
              )}
              {createStudentMut.isPending ? "Creating..." : "Create Student"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Track / Course — the SAME picker and the SAME endpoints the team-leader page
          uses. This page previously fed its pickers from GET /api/Courses (ownership-scoped, so a
          ContentCreator+TeamLeader saw only their own authored courses) and posted to the
          SystemAdmin-only /api/groups/{id}/courses, which 403'd for them. Both are gone. */}
      <AssignableContentPicker
        kind="track"
        groupId={groupId}
        groupName={group?.name}
        open={assignTrackOpen}
        onOpenChange={setAssignTrackOpen}
        onSelect={(id) => assignTrackMut.mutate(id)}
        isPending={assignTrackMut.isPending}
        pendingId={assignTrackMut.variables ?? null}
      />

      <AssignableContentPicker
        kind="course"
        groupId={groupId}
        groupName={group?.name}
        open={assignCourseOpen}
        onOpenChange={setAssignCourseOpen}
        onSelect={(id) => assignCourseMut.mutate(id)}
        isPending={assignCourseMut.isPending}
        pendingId={assignCourseMut.variables ?? null}
      />

      {/* Add Student Dialog */}
      <Dialog open={addStudentOpen} onOpenChange={setAddStudentOpen}>
        <DialogContent className="sm:max-w-md bg-card border rounded-2xl shadow-lg animate-in zoom-in-95 duration-200">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-primary" /> Add Student to Group
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or email..."
                value={addStudentSearchInput}
                onChange={(e) => setAddStudentSearchInput(e.target.value)}
                className="pl-9 rounded-xl text-foreground"
              />
            </div>
            <div className="max-h-72 overflow-y-auto divide-y border rounded-xl">
              {isAddStudentPickerLoading ? (
                <div className="p-6 text-center text-sm text-muted-foreground flex items-center justify-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" /> Searching...
                </div>
              ) : availableStudents.length === 0 ? (
                <div className="p-6 text-center text-sm text-muted-foreground">
                  {addStudentSearch ? "No matching students." : "No students available to add."}
                </div>
              ) : (
                availableStudents.map((u: any) => (
                  <button
                    key={u.id}
                    disabled={addStudentMut.isPending}
                    onClick={() => addStudentMut.mutate(u.id)}
                    className="w-full flex items-center justify-between gap-2 p-3 text-left hover:bg-primary/5 transition-colors disabled:opacity-50 cursor-pointer"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="h-8 w-8 bg-primary/10 text-primary rounded-full flex items-center justify-center font-bold text-xs shrink-0 uppercase">
                        {(u.fullName || u.email || "?").charAt(0)}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">
                          {u.fullName || u.email || "Unknown"}
                        </p>
                        {u.fullName && u.email && (
                          <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                        )}
                      </div>
                    </div>
                    {addStudentMut.isPending && addStudentMut.variables === u.id ? (
                      <Loader2 className="h-4 w-4 animate-spin text-primary shrink-0" />
                    ) : (
                      <Plus className="h-4 w-4 text-muted-foreground shrink-0" />
                    )}
                  </button>
                ))
              )}
            </div>
            {addStudentPickerData.totalCount > addStudentPickerPageSize && (
              <div className="flex items-center justify-between text-xs text-muted-foreground pt-1">
                <span>
                  Page {addStudentPickerPage} of {addStudentTotalPages} &middot;{" "}
                  {addStudentPickerData.totalCount} students
                </span>
                <div className="flex gap-1.5">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 rounded-lg text-xs px-2"
                    disabled={addStudentPickerPage <= 1}
                    onClick={() => setAddStudentPickerPage((p) => Math.max(1, p - 1))}
                  >
                    Prev
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 rounded-lg text-xs px-2"
                    disabled={addStudentPickerPage >= addStudentTotalPages}
                    onClick={() => setAddStudentPickerPage((p) => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddStudentOpen(false)} className="rounded-xl">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
